'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppDropdownMenu } from '@/components/ui/app-dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Archive02Icon, Cancel01Icon, Copy01Icon, Download01Icon, Edit03IconSolid, MoreHorizontalIcon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { UnavailableWalletBadge } from '@/components/ui/unavailable-wallet-badge';
import { WalletBalanceDisplay } from '@/components/wallet/wallet-balance-display';
import { cn } from '@/lib/ui/utils';
import { copyWalletAddress } from '@/lib/wallet/copy-address';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import type { Wallet } from '@/types/auth';
function truncateAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}
interface WalletListProps {
  wallets: Wallet[];
  activeWalletId: string | null;
  onSelect: (walletId: string) => void;
  onRename: (params: {
    walletId: string;
    label: string;
  }) => void;
  onArchive: (wallet: Wallet) => void;
  onExport: (wallet: Wallet) => void;
  onDisconnect: (wallet: Wallet) => void;
  /** Override the row's surface class. Defaults to `bg-background-secondary`
   *  (correct on `bg-background-primary` parents like `<Panel>`). Pass
   *  `bg-background-primary` when the parent surface is `bg-background-secondary`
   *  (e.g. inside a SilkSheet body) so rows contrast against the modal canvas. */
  rowClassName?: string;
}
interface WalletRowProps {
  wallet: Wallet;
  isActive: boolean;
  onSelect: () => void;
  onRename: (params: {
    walletId: string;
    label: string;
  }) => void;
  onArchive: () => void;
  onExport: () => void;
  onDisconnect: () => void;
  multiWalletsEnabled: boolean;
  isPrimaryWallet: boolean;
  rowClassName?: string;
}
function WalletRow(t0) {
  const $ = _c(75);
  const {
    wallet,
    isActive,
    onSelect,
    onRename,
    onArchive,
    onExport,
    onDisconnect,
    multiWalletsEnabled,
    isPrimaryWallet,
    rowClassName
  } = t0;
  const t = useTranslations("wallet.table");
  const tArchive = useTranslations("wallet.archive");
  const tUnlink = useTranslations("wallet.unlink");
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(wallet.label);
  const isAvailable = wallet.isAvailable !== false;
  const canManageWallet = multiWalletsEnabled || isPrimaryWallet;
  let t1;
  if ($[0] !== wallet.label) {
    t1 = function startEditing() {
      setEditValue(wallet.label);
      setIsEditing(true);
    };
    $[0] = wallet.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const startEditing = t1;
  const t2 = isActive || isEditing || !isAvailable ? "cursor-default" : "cursor-pointer";
  const t3 = !isAvailable && "opacity-60";
  let t4;
  if ($[2] !== rowClassName || $[3] !== t2 || $[4] !== t3) {
    t4 = cn("relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-panel-row bg-background-secondary px-4 py-3 sm:flex sm:gap-4", t2, t3, rowClassName);
    $[2] = rowClassName;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isActive || $[7] !== isAvailable || $[8] !== isEditing || $[9] !== multiWalletsEnabled || $[10] !== onSelect || $[11] !== t || $[12] !== wallet.label) {
    t5 = multiWalletsEnabled ? <button type="button" disabled={isEditing || isActive || !isAvailable} tabIndex={isEditing || isActive || !isAvailable ? -1 : 0} aria-label={t("select_wallet", {
      label: wallet.label
    })} aria-current={isActive ? "true" : undefined} aria-disabled={!isAvailable} onClick={onSelect} className="absolute inset-0 rounded-panel-row transition-colors hover:bg-background-tertiary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary focus-visible:ring-offset-2" /> : null;
    $[6] = isActive;
    $[7] = isAvailable;
    $[8] = isEditing;
    $[9] = multiWalletsEnabled;
    $[10] = onSelect;
    $[11] = t;
    $[12] = wallet.label;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== canManageWallet || $[15] !== editValue || $[16] !== isAvailable || $[17] !== isEditing || $[18] !== onRename || $[19] !== startEditing || $[20] !== t || $[21] !== wallet.address || $[22] !== wallet.id || $[23] !== wallet.label) {
    t6 = isEditing ? <div className="relative z-10 flex min-h-11 flex-1 items-center"><InputField size="sm" value={editValue} onChange={e => {
        setEditValue(e.target.value);
      }} onKeyDown={e_0 => {
        if (e_0.key === "Enter") {
          const trimmed = editValue.trim();
          if (trimmed.length > 0) {
            onRename({
              walletId: wallet.id,
              label: trimmed
            });
          }
          setIsEditing(false);
        } else {
          if (e_0.key === "Escape") {
            setEditValue(wallet.label);
            setIsEditing(false);
          }
        }
      }} autoFocus={true} /></div> : <div className="pointer-events-none relative z-10 flex min-h-11 min-w-0 flex-1 items-center"><div className="flex min-w-0 flex-1 flex-col items-start text-left leading-tight"><div className={cn("relative flex w-fit max-w-full min-w-0 items-center gap-1.5", canManageWallet && "sm:pr-11")}><span className="min-w-0 truncate text-body font-medium text-foreground-primary">{wallet.label}</span>{!isAvailable ? <UnavailableWalletBadge label={t("unavailable_on_device")} /> : null}{canManageWallet ? <button type="button" onClick={startEditing} aria-label={t("name")} className="pointer-events-auto absolute right-0 top-1/2 hidden size-11 -translate-y-1/2 items-center justify-center rounded-item text-foreground-secondary hover:text-foreground-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary sm:flex"><Icon icon={Edit03IconSolid} className="size-3" aria-hidden="true" /></button> : null}</div><button type="button" aria-label={t("copy_address")} title={wallet.address} onClick={() => {
          copyWalletAddress(wallet.address, wallet.label);
        }} className="pointer-events-auto max-w-full min-w-0 truncate rounded-item text-left text-caption text-foreground-secondary transition-colors hover:text-foreground-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{truncateAddress(wallet.address)}</button></div></div>;
    $[14] = canManageWallet;
    $[15] = editValue;
    $[16] = isAvailable;
    $[17] = isEditing;
    $[18] = onRename;
    $[19] = startEditing;
    $[20] = t;
    $[21] = wallet.address;
    $[22] = wallet.id;
    $[23] = wallet.label;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== wallet.address) {
    t7 = <div className="pointer-events-none relative z-10 col-span-2 row-start-2 w-full sm:order-2 sm:col-auto sm:row-auto sm:w-auto sm:shrink-0"><WalletBalanceDisplay address={wallet.address} variant="compact-split" forceIncludeHoldings={true} align="right" className="w-full flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-left sm:w-auto sm:flex-col sm:items-end sm:justify-start sm:gap-0 sm:text-right" primaryClassName="text-body font-medium text-foreground-primary" secondaryClassName="justify-start sm:justify-end" /></div>;
    $[25] = wallet.address;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t) {
    t8 = t("actions");
    $[27] = t;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <AnimatedButton variant="tertiary" prefix={MoreHorizontalIcon} aria-label={t8} />;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== canManageWallet || $[32] !== startEditing || $[33] !== t) {
    t10 = canManageWallet ? [{
      label: t("name"),
      icon: Edit03IconSolid,
      onSelect: startEditing
    }] as const : [] as const;
    $[31] = canManageWallet;
    $[32] = startEditing;
    $[33] = t;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t) {
    t11 = t("copy_address");
    $[35] = t;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== wallet.address || $[38] !== wallet.label) {
    t12 = () => {
      copyWalletAddress(wallet.address, wallet.label);
    };
    $[37] = wallet.address;
    $[38] = wallet.label;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12) {
    t13 = {
      label: t11,
      icon: Copy01Icon,
      onSelect: t12
    };
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== canManageWallet || $[44] !== onExport || $[45] !== t || $[46] !== wallet.type) {
    t14 = (wallet.type === "embedded" || wallet.type === "imported") && canManageWallet ? [{
      label: t("export_key"),
      icon: Download01Icon,
      onSelect: onExport
    }] as const : [] as const;
    $[43] = canManageWallet;
    $[44] = onExport;
    $[45] = t;
    $[46] = wallet.type;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== multiWalletsEnabled || $[49] !== onDisconnect || $[50] !== tUnlink || $[51] !== wallet.isPrimary || $[52] !== wallet.type) {
    t15 = multiWalletsEnabled && wallet.type === "external" && !wallet.isPrimary ? [{
      label: tUnlink("external_title"),
      icon: Cancel01Icon,
      variant: "destructive" as const,
      onSelect: onDisconnect
    }] as const : [] as const;
    $[48] = multiWalletsEnabled;
    $[49] = onDisconnect;
    $[50] = tUnlink;
    $[51] = wallet.isPrimary;
    $[52] = wallet.type;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== multiWalletsEnabled || $[55] !== onArchive || $[56] !== tArchive || $[57] !== wallet.isPrimary || $[58] !== wallet.type) {
    t16 = multiWalletsEnabled && !wallet.isPrimary && wallet.type !== "external" ? [{
      label: tArchive("title"),
      icon: Archive02Icon,
      onSelect: onArchive
    }] as const : [] as const;
    $[54] = multiWalletsEnabled;
    $[55] = onArchive;
    $[56] = tArchive;
    $[57] = wallet.isPrimary;
    $[58] = wallet.type;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t10 || $[61] !== t13 || $[62] !== t14 || $[63] !== t15 || $[64] !== t16) {
    t17 = [...t10, t13, ...t14, ...t15, ...t16];
    $[60] = t10;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== t17 || $[67] !== t9) {
    t18 = <div className="relative z-10 col-start-2 row-start-1 justify-self-end sm:order-3 sm:col-auto sm:row-auto"><AppDropdownMenu align="end" trigger={t9} items={t17} /></div>;
    $[66] = t17;
    $[67] = t9;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== t18 || $[70] !== t4 || $[71] !== t5 || $[72] !== t6 || $[73] !== t7) {
    t19 = <div className={t4}>{t5}{t6}{t7}{t18}</div>;
    $[69] = t18;
    $[70] = t4;
    $[71] = t5;
    $[72] = t6;
    $[73] = t7;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  return t19;
}
export function WalletList(t0) {
  const $ = _c(31);
  const {
    wallets,
    activeWalletId,
    onSelect,
    onRename,
    onArchive,
    onExport,
    onDisconnect,
    rowClassName
  } = t0;
  const t = useTranslations("wallet.table");
  const {
    enabled: multiWalletsEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  let t1;
  if ($[0] !== multiWalletsEnabled || $[1] !== primaryWallet || $[2] !== wallets) {
    t1 = multiWalletsEnabled ? wallets : primaryWallet === null ? [] : wallets.filter(wallet => wallet.id === primaryWallet.id);
    $[0] = multiWalletsEnabled;
    $[1] = primaryWallet;
    $[2] = wallets;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const visibleWallets = t1;
  if (visibleWallets.length === 0) {
    let t2;
    if ($[4] !== t) {
      t2 = t("name");
      $[4] = t;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== t2) {
      t3 = <div className="flex flex-col items-center gap-2 py-8"><p className="text-body text-foreground-secondary">{t2}</p></div>;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t2;
  if ($[8] !== activeWalletId || $[9] !== multiWalletsEnabled || $[10] !== onArchive || $[11] !== onDisconnect || $[12] !== onExport || $[13] !== onRename || $[14] !== onSelect || $[15] !== primaryWallet?.id || $[16] !== rowClassName || $[17] !== visibleWallets) {
    let t3;
    if ($[19] !== activeWalletId || $[20] !== multiWalletsEnabled || $[21] !== onArchive || $[22] !== onDisconnect || $[23] !== onExport || $[24] !== onRename || $[25] !== onSelect || $[26] !== primaryWallet?.id || $[27] !== rowClassName) {
      t3 = wallet_0 => <WalletRow key={wallet_0.id} wallet={wallet_0} isActive={wallet_0.id === activeWalletId} onSelect={() => {
        onSelect(wallet_0.id);
      }} onRename={onRename} onArchive={() => {
        onArchive(wallet_0);
      }} onExport={() => {
        onExport(wallet_0);
      }} onDisconnect={() => {
        onDisconnect(wallet_0);
      }} multiWalletsEnabled={multiWalletsEnabled} isPrimaryWallet={wallet_0.id === primaryWallet?.id} {...rowClassName !== undefined ? {
        rowClassName
      } : {}} />;
      $[19] = activeWalletId;
      $[20] = multiWalletsEnabled;
      $[21] = onArchive;
      $[22] = onDisconnect;
      $[23] = onExport;
      $[24] = onRename;
      $[25] = onSelect;
      $[26] = primaryWallet?.id;
      $[27] = rowClassName;
      $[28] = t3;
    } else {
      t3 = $[28];
    }
    t2 = visibleWallets.map(t3);
    $[8] = activeWalletId;
    $[9] = multiWalletsEnabled;
    $[10] = onArchive;
    $[11] = onDisconnect;
    $[12] = onExport;
    $[13] = onRename;
    $[14] = onSelect;
    $[15] = primaryWallet?.id;
    $[16] = rowClassName;
    $[17] = visibleWallets;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[29] !== t2) {
    t3 = <div className="flex flex-col gap-2">{t2}</div>;
    $[29] = t2;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  return t3;
}